import { jackpotPoolLetters } from '@/shared/theme/oro-mx-jackpot-assets';

export type JackpotReelBall = {
  index: number;
  letter: string;
  poolKey: string;
};

export type JackpotReelTarget = {
  index: number;
  letter: string;
  poolKey: string;
};

/** 履带默认带上已点亮格，齐完后条长不变，空转绕回才不会越跳越频。 */
export function generateJackpotReelBalls(
  pools: Array<{ collected: boolean[]; poolKey: string }>,
  options?: { includeCollected?: boolean },
): JackpotReelBall[] {
  const includeCollected = options?.includeCollected !== false;
  const balls: JackpotReelBall[] = [];
  for (const pool of pools) {
    const letters = jackpotPoolLetters(pool.poolKey);
    for (let index = 0; index < letters.length; index += 1) {
      if (!includeCollected && pool.collected[index] === true) continue;
      balls.push({ index, letter: letters[index] ?? '', poolKey: pool.poolKey });
    }
  }
  return balls;
}

export const JACKPOT_REEL_VISIBLE_COUNT = 5;
export const JACKPOT_REEL_IDLE_COPIES = 2;
export const JACKPOT_REEL_SPIN_COPIES = 6;
/** 源站桌面槽位 140px；空转 sv(speed:1)=1px/帧，抽奖 speed:20。 */
export const JACKPOT_REEL_SLOT = 1.4;
export const JACKPOT_REEL_SOURCE_SLOT_PX = 140;
export const JACKPOT_REEL_IDLE_SPEED = 1;
export const JACKPOT_REEL_SPIN_SPEED = 20;

/** 按实测槽宽缩放，让「格/秒」对齐源站 140px 槽，避免弹窗更小时看起来转得更快。 */
export function jackpotReelFrameDelta(ballSize: number, sourcePxPerFrame: number): number {
  if (ballSize <= 0) return sourcePxPerFrame;
  return sourcePxPerFrame * (ballSize / JACKPOT_REEL_SOURCE_SLOT_PX);
}

/**
 * 源站 JackpotScroller 初始 translateX：可见 5 格时把下标 0 放到窗口左侧，
 * 中心格留给停格（idle 的 JackpotScrollerMove 不用这个值）。
 */
export function jackpotReelSpinStartOffset(
  ballSize: number,
  visibleCount = JACKPOT_REEL_VISIBLE_COUNT,
): number {
  if (ballSize <= 0) return 0;
  return -Math.floor(visibleCount / 2) * ballSize;
}

/** 源站 ev()：量到一份宽度后 c <= -r 则回到 0。量不到宽时不挪，避免带子滑出窗口。 */
export function jackpotReelIdleWrap(offset: number, copyWidth: number): number {
  if (copyWidth <= 0) return offset;
  return offset <= -copyWidth ? 0 : offset;
}

/** 源站 JackpotScroller rolling：|translateX| > 2 份数据宽度时加回一份。 */
export function jackpotReelSpinWrap(offset: number, copyWidth: number): number {
  if (copyWidth <= 0) return offset;
  return Math.abs(offset) > 2 * copyWidth ? offset + copyWidth : offset;
}

/**
 * 源站 JackpotScroller.stop：把目标球停在可见区正中。
 * visibleCount 默认 5，中心下标 2。
 */
export function jackpotReelStopOffset(
  balls: JackpotReelBall[],
  target: JackpotReelTarget,
  ballSize: number,
  visibleCount = JACKPOT_REEL_VISIBLE_COUNT,
): number | null {
  if (ballSize <= 0 || balls.length === 0) return null;
  const found = balls.find(
    (ball) =>
      ball.poolKey.toLowerCase() === target.poolKey.toLowerCase()
      && ball.letter === target.letter
      && ball.index === target.index,
  );
  if (!found) return null;
  const index = balls.indexOf(found);
  const center = Math.floor(visibleCount / 2);
  let offset = -(index - center) * ballSize;
  const copyWidth = -balls.length * ballSize;
  if (offset > copyWidth) offset -= balls.length * ballSize;
  return offset;
}
